/**
 * useInvoices — cursor-paginated invoice list for the billing settings page.
 *
 * Wraps `billingAgent.getInvoices({ limit, cursor })` in the project's standard
 * loading / error / data shape, accumulating pages into a single `invoices`
 * array. `loadMore()` fetches the next page using the server's `next_cursor`;
 * `hasMore` reflects whether another page exists (i.e. `next_cursor != null`).
 * `refetch()` resets to the first page.
 *
 * Contract — GET /api/billing/invoices?limit=<n>&cursor=<id>:
 *   { invoices: [{ id, stripe_invoice_id, amount_cents, currency, status,
 *     hosted_invoice_url, period_start, period_end, created_at, paid_at }],
 *     next_cursor: <id|null> }
 *   Empty list when none / billing disabled.
 *
 * @param {object} [options]
 * @param {number} [options.limit=10] - Page size for each fetch.
 * @returns {{
 *   invoices: object[],
 *   loading: boolean,
 *   error: (string|null),
 *   hasMore: boolean,
 *   loadMore: () => Promise<void>,
 *   refetch: () => Promise<void>,
 * }}
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { billingAgent } from '../agents/billingAgent';

export function useInvoices({ limit = 10 } = {}) {
  const [invoices, setInvoices] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  // Guard against overlapping fetches (double-clicked "Load more").
  const inFlightRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  /**
   * Fetch one page. When `nextCursor` is null we are loading the first page and
   * replace the accumulated list; otherwise we append.
   * @param {string|number|null} nextCursor
   */
  const fetchPage = useCallback(async (nextCursor) => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    setLoading(true);
    setError(null);
    try {
      const data = await billingAgent.getInvoices({ limit, cursor: nextCursor });
      if (!mountedRef.current) return;
      const page = Array.isArray(data?.invoices) ? data.invoices : [];
      const newCursor = data?.next_cursor ?? null;
      setInvoices((prev) => (nextCursor ? [...prev, ...page] : page));
      setCursor(newCursor);
      setHasMore(newCursor != null);
    } catch (err) {
      if (mountedRef.current) {
        setError(err?.message || 'Failed to load your invoices');
      }
    } finally {
      inFlightRef.current = false;
      if (mountedRef.current) setLoading(false);
    }
  }, [limit]);

  // Initial load (first page) on mount and whenever the page size changes.
  useEffect(() => {
    fetchPage(null);
  }, [fetchPage]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchPage(cursor);
  }, [hasMore, loading, cursor, fetchPage]);

  const refetch = useCallback(async () => {
    await fetchPage(null);
  }, [fetchPage]);

  return { invoices, loading, error, hasMore, loadMore, refetch };
}

export default useInvoices;
